import React, { useEffect, useState } from 'react'
import { Alert, Button, Col, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row, Spinner } from 'reactstrap'
import { api } from '../../utils'
import { notify } from '../../utils/notify'

function toReferenceMonth(value) {
  if (!value) {
    return ''
  }

  return value.length === 7 ? `${value}-01` : value
}

function formatRecoveryError(error) {
  const responseData = error?.response?.data

  if (typeof responseData === 'string' && responseData.trim()) {
    return responseData
  }

  if (responseData?.error) {
    return responseData.error
  }

  if (responseData && typeof responseData === 'object') {
    const firstValue = Object.values(responseData)[0]
    if (Array.isArray(firstValue) && firstValue.length > 0) {
      return firstValue[0]
    }
    if (typeof firstValue === 'string' && firstValue.trim()) {
      return firstValue
    }
  }

  return 'Impossibile pianificare il recupero volumi.'
}

function buildRecoveryOutcomeMessage(data) {
  const summary = []

  if (data?.created_count) {
    summary.push(`${data.created_count} month${data.created_count === 1 ? '' : 's'} scheduled`)
  }
  if (data?.skipped_count) {
    summary.push(`${data.skipped_count} skipped`)
  }
  if (data?.blocked_count) {
    summary.push(`${data.blocked_count} blocked`)
  }

  if (summary.length === 0) {
    return {
      kind: 'warning',
      message: 'No recovery months were scheduled.',
    }
  }

  return {
    kind: data?.skipped_count || data?.blocked_count ? 'warning' : 'success',
    message: `Recovery result: ${summary.join(' | ')}.`,
  }
}

function VolumeRetrieveHistoricalRecovery({
  isOpen,
  onClose,
  scheduleId,
  initialDetails = null,
}) {
  const [volumeRetrieve, setVolumeRetrieve] = useState(initialDetails)
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState(null)
  const [recoveryMode, setRecoveryMode] = useState('single')
  const [recoveryMonth, setRecoveryMonth] = useState('')
  const [recoveryStartMonth, setRecoveryStartMonth] = useState('')
  const [recoveryEndMonth, setRecoveryEndMonth] = useState('')
  const [recoveryForce, setRecoveryForce] = useState(false)
  const [recoverySubmitting, setRecoverySubmitting] = useState(false)

  useEffect(() => {
    if (!isOpen) {
      setLoadError(null)
      setRecoveryMode('single')
      setRecoveryMonth('')
      setRecoveryStartMonth('')
      setRecoveryEndMonth('')
      setRecoveryForce(false)
      setRecoverySubmitting(false)
      return
    }

    if (!scheduleId) {
      setVolumeRetrieve(null)
      return
    }

    if (initialDetails) {
      setVolumeRetrieve(initialDetails)
      setLoading(false)
      return
    }

    let isMounted = true

    async function load() {
      setLoading(true)
      setLoadError(null)

      try {
        const response = await api.get(`/market-screener-detail/${scheduleId}`)
        if (isMounted) {
          setVolumeRetrieve(response.data)
        }
      } catch (error) {
        if (isMounted) {
          setLoadError('Error fetching market screener details.')
        }
      } finally {
        if (isMounted) {
          setLoading(false)
        }
      }
    }

    load()

    return () => {
      isMounted = false
    }
  }, [initialDetails, isOpen, scheduleId])

  const handleClose = () => {
    if (recoverySubmitting) {
      return
    }
    onClose()
  }

  const validateRecoveryRequest = () => {
    if (recoveryMode === 'single') {
      if (!recoveryMonth) {
        notify('Select a recovery month before continuing.', 'error')
        return false
      }

      return true
    }

    if (!recoveryStartMonth || !recoveryEndMonth) {
      notify('Select both recovery months before continuing.', 'error')
      return false
    }

    if (recoveryEndMonth < recoveryStartMonth) {
      notify('Recovery end month must be greater than or equal to the start month.', 'error')
      return false
    }

    return true
  }

  const submitRecoveryRequest = async () => {
    if (recoverySubmitting || !scheduleId || !validateRecoveryRequest()) {
      return
    }

    setRecoverySubmitting(true)

    try {
      const payload =
        recoveryMode === 'single'
          ? {
              reference_month: toReferenceMonth(recoveryMonth),
              force: recoveryForce,
            }
          : {
              reference_month_start: toReferenceMonth(recoveryStartMonth),
              reference_month_end: toReferenceMonth(recoveryEndMonth),
              force: recoveryForce,
            }

      const response = await api.post(`volume-report-scheduled/${scheduleId}/recover-month/`, payload)
      const outcome = buildRecoveryOutcomeMessage(response?.data)
      notify(outcome.message, outcome.kind)
      onClose()
    } catch (error) {
      notify(formatRecoveryError(error), 'error')
    } finally {
      setRecoverySubmitting(false)
    }
  }

  return (
    <Modal isOpen={isOpen} toggle={handleClose} size="lg" centered>
      <ModalHeader toggle={handleClose}>Historical Recovery</ModalHeader>
      <ModalBody>
        {loading ? <Spinner /> : null}
        {!loading && loadError ? <div className="text-center text-danger">{loadError}</div> : null}
        {!loading && !loadError ? (
          <>
            <Alert color="info" fade={false}>
              {recoveryMode === 'single'
                ? 'Schedule the recovery of one explicit historical month.'
                : 'All months between start and end are included. The range is inclusive.'}
            </Alert>
            <Row className="mb-4">
              <Col md={4}>
                <strong>Name</strong>
                <div>{volumeRetrieve?.name || '-'}</div>
              </Col>
              <Col md={4}>
                <strong>Cliente</strong>
                <div>{volumeRetrieve?.cliente?.nome || '-'}</div>
              </Col>
              <Col md={4}>
                <strong>Locale</strong>
                <div>{volumeRetrieve?.locale || '-'}</div>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col md={12}>
                <FormGroup tag="fieldset" className="mb-0">
                  <Label style={{ display: 'block' }}>Recovery mode</Label>
                  <FormGroup check inline>
                    <Input
                      type="radio"
                      id="recoveryModeSingle"
                      name="recoveryMode"
                      checked={recoveryMode === 'single'}
                      onChange={() => setRecoveryMode('single')}
                    />
                    <Label check htmlFor="recoveryModeSingle">
                      Single month
                    </Label>
                  </FormGroup>
                  <FormGroup check inline>
                    <Input
                      type="radio"
                      id="recoveryModeRange"
                      name="recoveryMode"
                      checked={recoveryMode === 'range'}
                      onChange={() => setRecoveryMode('range')}
                    />
                    <Label check htmlFor="recoveryModeRange">
                      Month range
                    </Label>
                  </FormGroup>
                </FormGroup>
              </Col>
            </Row>
            <Row>
              {recoveryMode === 'single' ? (
                <Col md={8}>
                  <FormGroup>
                    <Label for="referenceMonth" style={{ display: 'block' }}>
                      Reference month
                    </Label>
                    <Input
                      className="form-control-alternative"
                      type="month"
                      name="referenceMonth"
                      id="referenceMonth"
                      value={recoveryMonth}
                      onChange={(event) => setRecoveryMonth(event.target.value)}
                    />
                  </FormGroup>
                </Col>
              ) : (
                <>
                  <Col md={4}>
                    <FormGroup>
                      <Label for="recoveryStartMonth" style={{ display: 'block' }}>
                        Recovery start month
                      </Label>
                      <Input
                        className="form-control-alternative"
                        type="month"
                        name="recoveryStartMonth"
                        id="recoveryStartMonth"
                        value={recoveryStartMonth}
                        onChange={(event) => setRecoveryStartMonth(event.target.value)}
                      />
                    </FormGroup>
                  </Col>
                  <Col md={4}>
                    <FormGroup>
                      <Label for="recoveryEndMonth" style={{ display: 'block' }}>
                        Recovery end month
                      </Label>
                      <Input
                        className="form-control-alternative"
                        type="month"
                        name="recoveryEndMonth"
                        id="recoveryEndMonth"
                        value={recoveryEndMonth}
                        onChange={(event) => setRecoveryEndMonth(event.target.value)}
                      />
                    </FormGroup>
                  </Col>
                </>
              )}
              <Col md={4}>
                <FormGroup>
                  <Label htmlFor="recoveryForce" style={{ display: 'block' }}>
                    Force historical overwrite
                  </Label>
                  <Input
                    type="checkbox"
                    id="recoveryForce"
                    checked={recoveryForce}
                    onChange={(event) => setRecoveryForce(event.target.checked)}
                    style={{ width: 'auto', marginLeft: '0px' }}
                  />
                </FormGroup>
              </Col>
            </Row>
          </>
        ) : null}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" type="button" onClick={handleClose} disabled={recoverySubmitting}>
          Cancel
        </Button>
        <Button
          color="info"
          type="button"
          onClick={submitRecoveryRequest}
          disabled={loading || !!loadError || recoverySubmitting}
        >
          {recoverySubmitting
            ? 'Submitting...'
            : recoveryMode === 'single'
              ? 'Recover month'
              : 'Recover month range'}
        </Button>
      </ModalFooter>
    </Modal>
  )
}

export default VolumeRetrieveHistoricalRecovery
